import { z } from 'zod/v4';

// tenant/auth are NEVER part of these schemas. The gateway fills them from the request's
// AuthContext, and monitoring-scheduler's cluster-internal route takes `tenant` in its own
// request body (`MetricsRecomputeRequestSchema` in monitoring-service).

/**
 * Upper bound on requests/ids accepted in one call. Large selections must be
 * generated server-side under an operation id and streamed by `planShards`, not
 * sent over tRPC or through scheduler JSON. The cap bounds request
 * memory and the fan-out of enqueued jobs.
 */
export const MAX_METRICS_RECOMPUTE_REQUESTS_PER_CALL = 10_000;

export const MetricComputationRequestSchema = z.object({
    entityType: z.string(),
    entityId: z.string(),
    metricName: z.string(),
    effectiveAt: z.string(),
});

const recomputeTarget = {
    entityType: z.string(),
    groups: z.array(z.string()).min(1).optional(),
    reason: z.string().optional(),
};

export const MetricsRecomputeInputSchema = z.discriminatedUnion('scope', [
    z.object({
        ...recomputeTarget,
        scope: z.literal('all'),
        startWeek: z.string(),
        endWeek: z.string(),
    }),
    z.object({
        ...recomputeTarget,
        scope: z.literal('entities'),
        entityIds: z.array(z.string()).min(1).max(MAX_METRICS_RECOMPUTE_REQUESTS_PER_CALL),
    }),
]);

/** Per (group, week) result of a `MetricsService.recompute` call. */
export interface MetricsRecomputeOutcome {
    groupName: string;
    week: string;
    jobKey: string;
    /**
     * `enqueued`/`existing` for the `all` scope: whether this call planned the week or found it
     * already planned. `patched`/`absorbed`/`missing` for the `entities` scope: whether the ids
     * became new shards on the week's job, were already covered by a pending shard, or the week
     * lost its job between listing and patching.
     */
    outcome: 'enqueued' | 'existing' | 'patched' | 'absorbed' | 'missing' | 'error';
    error?: string;
}

export interface MetricsRecomputeResult {
    /**
     * The shared entity-refresh job; absent for the `entities` scope, which re-checks nothing.
     * `existing` means the bucket was already planned, so this call rode the periodic refresh
     * instead of crawling the source API itself.
     */
    entityRefresh?: { jobKey: string; effectiveAt: string; outcome: 'enqueued' | 'existing' };
    outcomes: MetricsRecomputeOutcome[];
    /**
     * Groups the `entities` scope reached no week for, because none of their weeks has a stored job
     * at the current version. A scoped call never creates one, so these entities stay stale until a
     * full recompute or the nightly fire plans the week.
     */
    skippedGroups?: string[];
}

/**
 * Pinned to {@link MetricsRecomputeResult} on both sides, so a field added to the interface
 * without the schema (or the reverse) is a compile error, not a contract mismatch in Sentry.
 */
export const MetricsRecomputeResultSchema: z.ZodType<MetricsRecomputeResult, MetricsRecomputeResult> = z.strictObject({
    entityRefresh: z
        .strictObject({
            jobKey: z.string(),
            effectiveAt: z.string(),
            outcome: z.enum(['enqueued', 'existing']),
        })
        .optional(),
    outcomes: z.array(
        z.strictObject({
            groupName: z.string(),
            week: z.string(),
            jobKey: z.string(),
            outcome: z.enum(['enqueued', 'existing', 'patched', 'absorbed', 'missing', 'error']),
            error: z.string().optional(),
        }),
    ),
    skippedGroups: z.array(z.string()).optional(),
});
